import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CaretLeft, CaretRight, MagnifyingGlassPlus } from "@phosphor-icons/react";
import { GALLERY, GALLERY_FILTERS } from "@/data/content";

const INITIAL = 9;

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [showAll, setShowAll] = useState(false);
  const [index, setIndex] = useState(null);

  const filtered = useMemo(
    () => (filter === "All" ? GALLERY : GALLERY.filter((g) => g.category === filter)),
    [filter]
  );
  const visible = showAll ? filtered : filtered.slice(0, INITIAL);

  const close = useCallback(() => setIndex(null), []);
  const next = useCallback(() => setIndex((i) => (i + 1) % filtered.length), [filtered.length]);
  const prev = useCallback(() => setIndex((i) => (i - 1 + filtered.length) % filtered.length), [filtered.length]);

  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [index, close, next, prev]);

  return (
    <section id="gallery" className="py-16 md:py-32 bg-background" data-testid="gallery-section">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-3">Our Work</p>
          <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-primary">
            A premium clean, in every detail
          </h2>
          <p className="mt-4 text-foreground/70 text-lg">
            Sample project imagery — soon to be replaced with real Explus Cleaning results.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {GALLERY_FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => { setFilter(f); setShowAll(false); }}
              className={`text-sm font-medium rounded-full px-4 py-2.5 min-h-[44px] border transition-all ${
                filter === f
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card text-foreground/70 border-border hover:border-primary/40 hover:-translate-y-0.5"
              }`}
              data-testid={`gallery-filter-${f.toLowerCase().replace(/\s|\//g, "-")}`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          <AnimatePresence>
            {visible.map((img, i) => {
              const realIndex = filtered.indexOf(img);
              return (
                <motion.button
                  key={img.src}
                  layout
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: (i % 3) * 0.05 }}
                  onClick={() => setIndex(realIndex)}
                  className="group relative mb-5 block w-full overflow-hidden rounded-2xl border border-border break-inside-avoid focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid={`gallery-item-${i}`}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/40 transition-colors duration-300 flex items-center justify-center">
                    <MagnifyingGlassPlus size={34} weight="bold" className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <span className="absolute bottom-3 left-3 rounded-full bg-black/50 backdrop-blur px-3 py-1 text-xs font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    {img.category}
                  </span>
                </motion.button>
              );
            })}
          </AnimatePresence>
        </div>

        {!showAll && filtered.length > INITIAL && (
          <div className="mt-10 text-center">
            <button
              onClick={() => setShowAll(true)}
              className="rounded-full bg-primary text-primary-foreground font-semibold px-8 py-4 hover:-translate-y-0.5 hover:shadow-lg transition-all"
              data-testid="view-full-gallery-btn"
            >
              View Full Gallery
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {index !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center"
            onClick={close}
            data-testid="lightbox"
          >
            <button onClick={close} className="absolute top-5 right-5 text-white/80 hover:text-white p-2" data-testid="lightbox-close">
              <X size={32} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-3 md:left-8 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              data-testid="lightbox-prev"
            >
              <CaretLeft size={28} />
            </button>
            <motion.figure
              key={filtered[index].src}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="max-w-[90vw] max-h-[85vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={filtered[index].src.replace("w=900", "w=1600")} alt={filtered[index].alt} className="max-w-[90vw] max-h-[80vh] object-contain rounded-xl" />
              <figcaption className="mt-3 text-center text-white/80 text-sm">
                {filtered[index].alt} · {index + 1} / {filtered.length}
              </figcaption>
            </motion.figure>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-3 md:right-8 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              data-testid="lightbox-next"
            >
              <CaretRight size={28} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
